import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParams } from '../../../common/queryParams/useQueryParams';
import {
  AlgorithmValidFormDataSchema,
  RequiredKeyDataType,
} from '../../schema';
import { AlgorithmViewPanel } from '../AlgorithmViewPanel';
import {
  AlgorithmFilter,
  useListAlgorithmsQuery,
} from '../query/useGetAlgorithmsQuery';
import {
  AlgorithmsTableView,
  AlgorithmsTableViewRef,
} from './AlgoritmsTableView';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { DataObjectType } from '../../types';
import { AlgorithmExtendedType } from '../schema/types';
import { TestCalculateAlgorithmsModal } from '../testRun/AlgorithmTestCalculateModal';
import { Form, useAlertContext } from '../../../common/form';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useBrokenReferences } from '../brokenReferences/useBrokenReferences';
import { useDataObjectFormLabelTranslation } from '../../useDataObjectFormLabelTranslation';
import styled from 'styled-components';
import { BrokenReferencesAlertWithDrawer } from '../brokenReferences/BrokenReferencesAlertWithDrawer';

const { t } = i18nNsFactory('algorithm-form-table');
interface AlgorithmsTableProps {
  onAlgorithmsCountChange?: (count: number) => void;
  requiredKeys?: RequiredKeyDataType[];
}

export const AlgorithmsTable: FC<AlgorithmsTableProps> = (props) => {
  const { onAlgorithmsCountChange, requiredKeys } = props;
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const { t: tLabel } = useDataObjectFormLabelTranslation();

  const [isAlgorithmView, setIsAlgorithmView] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [filter, setFilter] = useState<AlgorithmFilter>({
    statusCodes: ['ACTIVE'],
  });
  const [testCalculateAlgorithms, setTestCalculateAlgorithms] = useState<
    AlgorithmExtendedType[] | null
  >(null);

  const { data: algorithms = [], isFetching } = useListAlgorithmsQuery(
    {
      dataObjectVersionId: dataObjectDescriptionVersion.versionId,
      isAlgorithmView,
      ...filter,
    },
    {
      initialData: [],
    },
  );

  useEffect(() => {
    if (algorithms && onAlgorithmsCountChange) {
      onAlgorithmsCountChange(algorithms.length);
    }
  }, [algorithms, onAlgorithmsCountChange]);

  const [{ algorithmId, algorithmCode }] = useQueryParams();
  const algorithmIdFromCode =
    algorithmCode && algorithms.find((a) => a.code === algorithmCode)?.id;

  const tableViewRef = useRef<AlgorithmsTableViewRef>(null);
  const { resetAlert, setSuccess } = useAlertContext();

  const selectedAlgorithmIds = useMemo(
    () =>
      Array.from(selected)
        .map(
          (a) =>
            tableViewRef?.current?.getTable()?.getRowModel().rowsById[a]
              ?.original?.id,
        )
        .filter((id): id is number => id !== undefined),
    [selected],
  );
  const {
    brokenReferencesMap,
    refetch: refetchBrokenReferences,
    isFetching: isFetchingBrokenReferences,
  } = useBrokenReferences(selectedAlgorithmIds);

  const handleCheckBrokenReferencesSelectedRows = useCallback(async () => {
    resetAlert();
    await refetchBrokenReferences();
  }, [resetAlert, refetchBrokenReferences]);

  useEffect(() => {
    if (brokenReferencesMap?.size === 0) {
      setSuccess(t('alert.no-broken-references'));
    }
  }, [brokenReferencesMap?.size, setSuccess]);

  return (
    <Form schema={AlgorithmValidFormDataSchema}>
      <BrokenReferencesAlertFullWidth
        brokenReferencesMap={brokenReferencesMap}
        tLabel={tLabel}
      />
      <AlgorithmsTableView
        ref={tableViewRef}
        algorithms={algorithms}
        isLoading={isFetching || isFetchingBrokenReferences}
        onChangeView={setIsAlgorithmView}
        filter={filter}
        onFilter={setFilter}
        selected={selected}
        setSelected={setSelected}
        onTestRunSelectedRows={
          dataObjectDescriptionVersion?.dataObjectType === DataObjectType.OUT
            ? setTestCalculateAlgorithms
            : undefined
        }
        onCheckBrokenReferencesSelectedRows={
          handleCheckBrokenReferencesSelectedRows
        }
      />
      {(algorithmId || algorithmIdFromCode) && (
        <AlgorithmViewPanel
          algorithmId={
            algorithmIdFromCode ? algorithmIdFromCode : Number(algorithmId)
          }
          requiredKeys={requiredKeys}
        />
      )}
      {testCalculateAlgorithms && (
        <TestCalculateAlgorithmsModal
          algorithms={testCalculateAlgorithms}
          onClose={() => setTestCalculateAlgorithms(null)}
        />
      )}
    </Form>
  );
};
const BrokenReferencesAlertFullWidth = styled(BrokenReferencesAlertWithDrawer)`
  width: 100%;
`;
